import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../core/api';
import Tooltip from '../components/Tooltip';
import LeadDetailModal from './marketing-leads/LeadDetailModal';
import LeadEmailProposalModal from './marketing-leads/LeadEmailProposalModal';
import type { MarketingLead, Message } from './marketing-leads/types';
import { stateConfig, timeAgo } from './marketing-leads/types';

// ─── Types ───────────────────────────────────────────────────────────────────

interface Warmup {
  active: boolean;
  day: number;
  limit: number;
  nextLimit: number | null;
  daysToFull: number;
}

interface Stats {
  enabled: boolean;
  pending: number;
  sent: number;
  failed: number;
  replied: number;
  sentToday: number;
  dailyLimit: number;
  remainingToday: number;
  windowStart: number;
  windowEnd: number;
  replyRate: number;
  warmup: Warmup;
  fullLimit: number;
  noReplyRisk: boolean;
  noReplyAbortAfter: number;
}

const hour12 = (h: number) => `${h % 12 === 0 ? 12 : h % 12}${h < 12 ? 'am' : 'pm'}`;

// ─── Detector de País ────────────────────────────────────────────────────────
export function getCountryInfo(lead: MarketingLead): { code: string; name: string; flag: string; badgeClass: string } {
  const raw = (lead.phone || '').replace(/\D/g, '');
  const country = (lead.clinicCountry || '').toLowerCase();
  const city = (lead.clinicCity || '').toLowerCase();

  // Panamá (+507)
  if (raw.startsWith('507') || country.includes('panam') || city.includes('panam') || city.includes('chiriqui') || city.includes('costa del este') || city.includes('san francisco') || city.includes('obarrio') || city.includes('marbella') || city.includes('el dorado') || city.includes('el cangrejo') || city.includes('albrook') || city.includes('clayton') || city.includes('costa verde') || city.includes('chorrera')) {
    return {
      code: 'PA',
      name: 'Panamá',
      flag: '🇵🇦',
      badgeClass: 'bg-blue-50 text-blue-800 dark:bg-blue-950/70 dark:text-blue-300 border-blue-200 dark:border-blue-800'
    };
  }

  // Ecuador (+593)
  if (raw.startsWith('593') || country.includes('ecuador') || city.includes('quito') || city.includes('guayaquil') || city.includes('cuenca')) {
    return {
      code: 'EC',
      name: 'Ecuador',
      flag: '🇪🇨',
      badgeClass: 'bg-yellow-50 text-yellow-800 dark:bg-yellow-950/70 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800'
    };
  }

  // Costa Rica (+506)
  if (raw.startsWith('506') || country.includes('costa rica') || city.includes('san jose')) {
    return {
      code: 'CR',
      name: 'Costa Rica',
      flag: '🇨🇷',
      badgeClass: 'bg-red-50 text-red-800 dark:bg-red-950/70 dark:text-red-300 border-red-200 dark:border-red-800'
    };
  }

  // México (+52)
  if (raw.startsWith('52') || country.includes('mexic') || city.includes('cdmx')) {
    return {
      code: 'MX',
      name: 'México',
      flag: '🇲🇽',
      badgeClass: 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/70 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800'
    };
  }

  // Colombia (+57)
  if (raw.startsWith('57') || country.includes('colombia') || city.includes('bogota') || city.includes('medellin')) {
    return {
      code: 'CO',
      name: 'Colombia',
      flag: '🇨🇴',
      badgeClass: 'bg-amber-50 text-amber-800 dark:bg-amber-950/70 dark:text-amber-300 border-amber-200 dark:border-amber-800'
    };
  }

  // Default: República Dominicana (+1 809/829/849)
  return {
    code: 'DO',
    name: 'Rep. Dominicana',
    flag: '🇩🇴',
    badgeClass: 'bg-rose-50 text-rose-800 dark:bg-rose-950/70 dark:text-rose-300 border-rose-200 dark:border-rose-800'
  };
}

// ─── Plantilla de importación ────────────────────────────────────────────────

/**
 * Los números de ejemplo usan el rango 555-01xx, reservado para ficción, para
 * que nadie reciba un mensaje por copiar la plantilla sin editarla.
 *
 * Las dos últimas columnas las rellena el sistema al importar: van vacías en la
 * plantilla para que quede claro que no hay que tocarlas.
 */
const PLANTILLA_FILAS = [
  ['telefono', 'clinica', 'ciudad', 'notas', 'estado_diana', 'fecha_importado'],
  ['809-555-0101', 'Centro Odontológico Sonrisa', 'Santo Domingo', 'Google Maps', '', ''],
  ['829-555-0102', 'Dental Care Santiago', 'Santiago', 'Instagram', '', ''],
  ['849-555-0103', 'Clínica Dr. Pérez', 'La Vega', 'Referido', '', ''],
];

function descargarPlantilla() {
  const csv = PLANTILLA_FILAS
    .map(fila => fila.map(c => (/[",;\n]/.test(c) ? `"${c.replace(/"/g, '""')}"` : c)).join(','))
    .join('\r\n');

  // El BOM hace que Excel abra el archivo con los acentos correctos
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'plantilla-prospeccion-diana.csv';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ─── Main ────────────────────────────────────────────────────────────────────

const Outreach: React.FC = () => {
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

  const [rows, setRows] = useState('');
  const [showImport, setShowImport] = useState(false);
  const [detalle, setDetalle] = useState<
    { duplicados: string[]; invalidos: string[]; totalDup: number; totalInv: number } | null
  >(null);

  // Hoja de Google como origen permanente
  const [hoja, setHoja] = useState<{ url: string | null; ultima: any | null }>({ url: null, ultima: null });
  const [editandoHoja, setEditandoHoja] = useState(false);
  const [hojaInput, setHojaInput] = useState('');
  const [importing, setImporting] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  // Leads de prospección y conversaciones
  const [leads, setLeads] = useState<MarketingLead[]>([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [leadFilter, setLeadFilter] = useState<'all' | 'replied' | 'sent' | 'pending' | 'failed'>('all');
  const [countryFilter, setCountryFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const countryCounts = useMemo(() => {
    const counts: Record<string, number> = { all: leads.length, PA: 0, DO: 0, EC: 0, CR: 0, MX: 0, CO: 0, other: 0 };
    leads.forEach(l => {
      const c = getCountryInfo(l).code;
      if (counts[c] !== undefined) {
        counts[c]++;
      } else {
        counts.other++;
      }
    });
    return counts;
  }, [leads]);

  // Modal de detalle y chat
  const [selectedLead, setSelectedLead] = useState<MarketingLead | null>(null);
  const [emailProposalLead, setEmailProposalLead] = useState<MarketingLead | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [updatingState, setUpdatingState] = useState(false);
  const [convertingPlan, setConvertingPlan] = useState<'professional' | 'basic' | null>(null);

  const showToast = (msg: string, type: 'success' | 'error' = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const load = useCallback(async () => {
    try {
      const res = await api.get('/marketing-sales/outreach/stats');
      if (res.data?.success) {
        setStats(res.data);
        setLoadError(null);
      } else {
        setLoadError(res.data?.error || 'El servidor respondió sin datos de la campaña.');
      }
    } catch (err: any) {
      // Sin esto la página quedaba en blanco y no había forma de saber qué falló.
      setLoadError(
        err?.response?.status === 403
          ? 'Tu cuenta no tiene permiso para ver la prospección.'
          : err?.response?.data?.error || 'No se pudo cargar el estado de la campaña.'
      );
    } finally { setLoading(false); }
  }, []);

  const loadLeads = useCallback(async (status: string = 'all') => {
    setLeadsLoading(true);
    try {
      const res = await api.get(`/marketing-sales/outreach/leads?status=${status}&limit=200`);
      if (res.data?.success) {
        setLeads(res.data.data?.leads || []);
      }
    } catch (err) {
      console.error('Error fetching outreach leads:', err);
    } finally {
      setLeadsLoading(false);
    }
  }, []);

  const loadHoja = useCallback(async () => {
    try {
      const res = await api.get('/marketing-sales/outreach/sheet');
      if (res.data?.success) setHoja({ url: res.data.url, ultima: res.data.ultima });
    } catch { /* la hoja es opcional: sin ella la importacion manual sigue igual */ }
  }, []);

  useEffect(() => { load(); loadHoja(); }, [load, loadHoja]);
  useEffect(() => { loadLeads(leadFilter); }, [leadFilter, loadLeads]);

  const fetchMessages = async (phone: string) => {
    setLoadingMessages(true);
    setMessages([]);
    try {
      const res = await api.get(`/marketing-sales/leads/${encodeURIComponent(phone)}/messages`);
      if (res.data?.success) {
        setMessages(res.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching messages:', err);
    } finally {
      setLoadingMessages(false);
    }
  };

  const openLead = (lead: MarketingLead) => {
    setSelectedLead(lead);
    fetchMessages(lead.phone);
  };

  const handleStateChange = async (phone: string, newState: string) => {
    setUpdatingState(true);
    try {
      const res = await api.put(`/marketing-sales/leads/${encodeURIComponent(phone)}/state`, { state: newState });
      if (res.data?.success) {
        setLeads(prev => prev.map(l => l.phone === phone ? { ...l, state: newState } : l));
        setSelectedLead(prev => prev?.phone === phone ? { ...prev, state: newState } : prev);
        showToast('Estado actualizado', 'success');
      }
    } catch {
      showToast('Error al actualizar estado', 'error');
    } finally {
      setUpdatingState(false);
    }
  };

  const handleConvert = async (phone: string, plan: 'professional' | 'basic') => {
    setConvertingPlan(plan);
    try {
      const res = await api.post(`/marketing-sales/leads/${encodeURIComponent(phone)}/convert`, { plan });
      if (res.data?.success) {
        const now = new Date().toISOString();
        setLeads(prev => prev.map(l => l.phone === phone ? { ...l, state: 'PLAN_ACTIVE', convertedAt: now, conversionPlan: plan } : l));
        setSelectedLead(prev => prev?.phone === phone ? { ...prev, state: 'PLAN_ACTIVE', convertedAt: now, conversionPlan: plan } : prev);
        showToast('Plan activado', 'success');
      }
    } catch {
      showToast('Error al convertir lead', 'error');
    } finally {
      setConvertingPlan(null);
    }
  };


  const guardarHoja = async () => {
    setBusy('hoja');
    try {
      const res = await api.post('/marketing-sales/outreach/sheet', { url: hojaInput });
      showToast(hojaInput.trim()
        ? `Hoja conectada · ${res.data.leidas} leads legibles`
        : 'Hoja desconectada');
      setEditandoHoja(false);
      loadHoja();
    } catch (err: any) {
      showToast(err?.response?.data?.error || 'No se pudo conectar la hoja', 'error');
    } finally { setBusy(null); }
  };

  const sincronizarHoja = async () => {
    setBusy('sync');
    try {
      const res = await api.post('/marketing-sales/outreach/sheet/sync');
      const d = res.data;
      showToast(
        `${d.imported} leads nuevos` +
        (d.duplicates ? ` · ${d.duplicates} ya estaban` : '') +
        (d.invalid ? ` · ${d.invalid} sin teléfono` : '')
      );
      loadHoja(); load();
    } catch (err: any) {
      showToast(err?.response?.data?.error || 'No se pudo leer la hoja', 'error');
    } finally { setBusy(null); }
  };

  const doImport = async () => {
    if (!rows.trim()) return;
    setImporting(true);
    try {
      const res = await api.post('/marketing-sales/outreach/import', { rows });
      const d = res.data;
      showToast(
        `${d.imported} importados` +
        (d.duplicates ? ` · ${d.duplicates} ya existían` : '') +
        (d.invalid ? ` · ${d.invalid} descartados` : '')
      );
      // Detalle de lo descartado: sin esto solo se veia un numero y no habia
      // forma de saber que filas hay que revisar.
      setDetalle(
        (d.duplicates || d.invalid)
          ? { duplicados: d.duplicateRows || [], invalidos: d.invalidRows || [],
              totalDup: d.duplicates || 0, totalInv: d.invalid || 0 }
          : null
      );
      setRows(''); setShowImport(false); load();
    } catch (err: any) {
      showToast(err?.response?.data?.error || 'No se pudo importar', 'error');
    } finally { setImporting(false); }
  };

  const toggleCampaign = async () => {
    if (!stats) return;
    const next = !stats.enabled;
    if (next && !window.confirm(
      `Se enviarán hasta ${stats.dailyLimit} mensajes por día, de ${hour12(stats.windowStart)} a ${hour12(stats.windowEnd)} en días hábiles.\n\n¿Activar la campaña?`
    )) return;
    setBusy('toggle');
    try {
      await api.post('/marketing-sales/outreach/toggle', { enabled: next });
      showToast(next ? 'Campaña activada' : 'Campaña pausada');
      load();
    } catch { showToast('No se pudo cambiar el estado', 'error'); }
    finally { setBusy(null); }
  };

  const toggleWarmup = async () => {
    if (!stats) return;
    const start = !stats.warmup.active;
    if (start && !window.confirm(
      'El calentamiento limita el envío a 3 mensajes al día durante los primeros 2 días, luego 8, y al cuarto día pasa al ritmo completo.\n\n¿Iniciar hoy?'
    )) return;
    setBusy('warmup');
    try {
      await api.post('/marketing-sales/outreach/warmup', { start });
      showToast(start ? 'Calentamiento iniciado' : 'Calentamiento desactivado');
      load();
    } catch { showToast('No se pudo configurar', 'error'); }
    finally { setBusy(null); }
  };

  const runNow = async () => {
    if (!window.confirm('Enviar ahora el lote de hoy, ignorando el horario. ¿Continuar?')) return;
    setBusy('run');
    try {
      const res = await api.post('/marketing-sales/outreach/run');
      showToast(res.data?.message || 'Lote iniciado');
      setTimeout(load, 4000);
    } catch (err: any) {
      showToast(err?.response?.data?.error || 'No se pudo iniciar', 'error');
    } finally { setBusy(null); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-slate-300">
        <span className="material-symbols-outlined animate-spin text-[36px]">progress_activity</span>
      </div>
    );
  }
  if (!stats) {
    return (
      <div className="px-4 pt-6 pb-10 max-w-3xl mx-auto">
        <h1 className="text-xl font-bold text-slate-900 dark:text-white mb-4">Prospección</h1>
        <div className="rounded-2xl border border-amber-200 dark:border-amber-900/40 bg-amber-50 dark:bg-amber-950/20 px-4 py-4 flex items-start gap-3">
          <span className="material-symbols-outlined text-[20px] text-amber-500 shrink-0 mt-0.5">error</span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-900 dark:text-amber-300">
              No se pudo cargar la prospección
            </p>
            <p className="text-xs text-amber-700/80 dark:text-amber-400/80 mt-0.5 leading-relaxed">
              {loadError || 'Error desconocido.'}
            </p>
            <button
              onClick={() => { setLoading(true); load(); }}
              className="mt-3 px-3 py-1.5 rounded-lg bg-amber-500 text-white text-xs font-semibold hover:bg-amber-600 transition-colors"
            >
              Reintentar
            </button>
          </div>
        </div>
      </div>
    );
  }

  const total = stats.pending + stats.sent + stats.failed;
  const progress = total > 0 ? Math.round((stats.sent / total) * 100) : 0;

  return (
    <div className="px-4 pt-6 pb-10 max-w-3xl mx-auto space-y-5">

      {toast && (
        <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-4 py-2.5 rounded-xl text-white text-sm font-medium shadow-lg ${
          toast.type === 'success' ? 'bg-slate-900 dark:bg-white dark:text-slate-900' : 'bg-red-500'
        }`}>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900 dark:text-white flex items-center">
            Prospección
            {stats.enabled && <span className="ml-2 w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />}
          </h1>
          <p className="text-xs text-slate-400 mt-0.5">Primer contacto a listas importadas</p>
        </div>
        <button
          onClick={() => { setLoading(true); load(); }}
          className="w-8 h-8 rounded-lg flex items-center justify-center border border-slate-200 dark:border-slate-700 text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
        >
          <span className="material-symbols-outlined text-[18px]">refresh</span>
        </button>
      </div>

      {/* Freno de emergencia — aviso temprano */}
      {stats.noReplyRisk && (
        <div className="rounded-2xl border border-amber-200 dark:border-amber-900/40 bg-amber-50 dark:bg-amber-950/20 px-4 py-3 flex items-start gap-3">
          <span className="material-symbols-outlined text-[20px] text-amber-500 shrink-0 mt-0.5">warning</span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-900 dark:text-amber-300">Nadie ha respondido todavía</p>
            <p className="text-xs text-amber-700/80 dark:text-amber-400/80 mt-0.5 leading-relaxed">
              Llevas {stats.sent} mensajes sin una sola respuesta. Si llega a {stats.noReplyAbortAfter}, la campaña se
              detendrá sola para no gastar los contactos que quedan. Puede ser el mensaje, o que WhatsApp esté
              limitando la entrega de este número.
            </p>
          </div>
        </div>
      )}

      {/* Métricas */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'Pendientes',   value: stats.pending, icon: 'schedule',    color: 'text-slate-400' },
          { label: 'Enviados',     value: stats.sent,    icon: 'send',        color: 'text-indigo-500' },
          { label: 'Respondieron', value: stats.replied, icon: 'forum',       color: 'text-emerald-500' },
          { label: 'Fallidos',     value: stats.failed,  icon: 'error',       color: stats.failed > 0 ? 'text-red-500' : 'text-slate-400' },
        ].map(m => (
          <div key={m.label} className="rounded-2xl border border-slate-200 dark:border-white/8 bg-white dark:bg-slate-card p-4">
            <span className={`material-symbols-outlined text-[20px] ${m.color}`}>{m.icon}</span>
            <p className="text-xl font-bold text-slate-800 dark:text-white mt-1">{m.value}</p>
            <p className="text-[11px] text-slate-400 mt-0.5">{m.label}</p>
          </div>
        ))}
      </div>

      {/* Progreso */}
      {total > 0 && (
        <div className="rounded-2xl border border-slate-200 dark:border-white/8 bg-white dark:bg-slate-card p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold text-slate-800 dark:text-white">
              {stats.sent} de {total} contactados
            </p>
            {stats.sent > 0 && (
              <p className="text-xs text-slate-400">
                Respuesta: <span className="font-semibold text-emerald-600 dark:text-emerald-400">{stats.replyRate}%</span>
              </p>
            )}
          </div>
          <div className="h-1.5 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
            <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${progress}%` }} />
          </div>
          <p className="text-[11px] text-slate-400 mt-2 flex items-center flex-wrap">
            {stats.sentToday} enviados hoy · quedan {stats.remainingToday} de {stats.dailyLimit}
            <Tooltip
              text={`Se envían pocos por día y solo en horario hábil (${hour12(stats.windowStart)} a ${hour12(stats.windowEnd)}) porque WhatsApp bloquea números que mandan muchos mensajes iguales a desconocidos de golpe.`}
            />
          </p>
        </div>
      )}

      {/* Calentamiento del número */}
      <div className={`rounded-2xl border p-4 ${
        stats.warmup.active
          ? 'border-indigo-200 dark:border-indigo-900/40 bg-indigo-50 dark:bg-indigo-950/20'
          : 'border-slate-200 dark:border-white/8 bg-white dark:bg-slate-card'
      }`}>
        <div className="flex items-start gap-3">
          <span className={`material-symbols-outlined text-[20px] shrink-0 mt-0.5 ${stats.warmup.active ? 'text-indigo-500' : 'text-slate-400'}`}>
            thermostat
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-900 dark:text-white flex items-center">
              Calentamiento del número
              <Tooltip
                text="Un número nuevo que el primer día manda 15 mensajes a desconocidos es el patrón que WhatsApp más castiga. El calentamiento sube el ritmo poco a poco para que el número no levante sospechas."
                example="Días 1-2: 3 mensajes · Días 3-4: 8 · Después: ritmo completo."
              />
            </p>
            {stats.warmup.active ? (
              <p className="text-xs text-indigo-700/80 dark:text-indigo-300/80 mt-1 leading-relaxed">
                Día {stats.warmup.day + 1} · enviando <strong>{stats.warmup.limit}/día</strong>
                {stats.warmup.nextLimit && <> · sube a {stats.warmup.nextLimit} pronto</>}
                {stats.warmup.daysToFull > 0 && <> · ritmo completo ({stats.fullLimit}/día) en {stats.warmup.daysToFull} {stats.warmup.daysToFull === 1 ? 'día' : 'días'}</>}
              </p>
            ) : (
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">
                Desactivado — enviando al ritmo completo de {stats.fullLimit}/día.
                Actívalo si el número es nuevo o lleva poco tiempo en uso.
              </p>
            )}
          </div>
          <button
            onClick={toggleWarmup}
            disabled={busy === 'warmup'}
            className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-all disabled:opacity-50 shrink-0 ${
              stats.warmup.active
                ? 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
                : 'bg-indigo-500 hover:bg-indigo-600 text-white'
            }`}
          >
            {stats.warmup.active ? 'Desactivar' : 'Iniciar'}
          </button>
        </div>
      </div>

      {/* Acciones */}
      <div className="flex flex-wrap items-center gap-2">
        {!showImport && (
          <button
            onClick={() => setShowImport(true)}
            className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-semibold bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
          >
            <span className="material-symbols-outlined text-[18px]">upload</span>
            Importar lista
          </button>
        )}

        {stats.pending > 0 && (
          <>
            <button
              onClick={toggleCampaign}
              disabled={busy === 'toggle'}
              className={`flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all disabled:opacity-50 ${
                stats.enabled
                  ? 'bg-amber-500 hover:bg-amber-600 text-white'
                  : 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-lg shadow-emerald-500/30'
              }`}
            >
              <span className="material-symbols-outlined text-[18px]">{stats.enabled ? 'pause' : 'play_arrow'}</span>
              {stats.enabled ? 'Pausar campaña' : 'Activar campaña'}
            </button>

            <button
              onClick={runNow}
              disabled={busy === 'run' || stats.remainingToday === 0}
              className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-semibold bg-slate-800 dark:bg-white text-white dark:text-slate-900 hover:opacity-90 transition-all disabled:opacity-40"
              title={stats.remainingToday === 0 ? 'Ya se alcanzó el límite de hoy' : ''}
            >
              <span className={`material-symbols-outlined text-[18px] ${busy === 'run' ? 'animate-spin' : ''}`}>
                {busy === 'run' ? 'progress_activity' : 'send'}
              </span>
              Enviar lote ahora
            </button>
          </>
        )}
      </div>

      {/* Importador */}
      {showImport && (
        <div className="rounded-2xl border border-slate-200 dark:border-white/8 bg-white dark:bg-slate-card p-4 space-y-2">
          <div className="flex items-start justify-between gap-3">
            <p className="text-sm font-semibold text-slate-900 dark:text-white flex items-center">
              Pega las filas desde Excel
              <Tooltip
                text="Copia las filas desde Excel o Google Sheets y pégalas aquí. Se aceptan comas, punto y coma o tabulaciones. Si incluyes la fila de encabezado, las columnas se reconocen por su nombre sin importar el orden. Los teléfonos se normalizan solos y los repetidos se ignoran."
                example="telefono, clinica, ciudad, notas"
              />
            </p>
            <button
              onClick={descargarPlantilla}
              className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
              title="Descarga un archivo de ejemplo con las columnas correctas"
            >
              <span className="material-symbols-outlined text-[16px]">download</span>
              Descargar plantilla
            </button>
          </div>

          <div className="rounded-xl bg-slate-50 dark:bg-slate-900/50 px-3 py-2 text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed">
            <span className="font-semibold text-slate-700 dark:text-slate-300">telefono</span> es la única obligatoria ·{' '}
            <span className="font-semibold text-slate-700 dark:text-slate-300">clinica</span> personaliza el mensaje ·{' '}
            <span className="font-semibold text-slate-700 dark:text-slate-300">ciudad</span> y{' '}
            <span className="font-semibold text-slate-700 dark:text-slate-300">notas</span> son opcionales.
            Las columnas de más se ignoran sin romper nada.
          </div>
          <textarea
            value={rows}
            onChange={e => setRows(e.target.value)}
            rows={8}
            placeholder={'809-555-1234, Centro Odontológico Sonrisa\n829-444-5566, Dental Care Santiago\n849-777-8899, Clínica Dr. Pérez'}
            className="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 dark:bg-slate-900 dark:text-white text-xs font-mono focus:outline-none focus:ring-2 focus:ring-emerald-400 resize-y"
          />
          <div className="flex items-center gap-2">
            <button
              onClick={doImport}
              disabled={importing || !rows.trim()}
              className="px-4 py-2.5 rounded-xl text-sm font-semibold bg-emerald-500 hover:bg-emerald-600 text-white transition-all disabled:opacity-50"
            >
              {importing ? 'Importando...' : 'Importar'}
            </button>
            <button
              onClick={() => { setShowImport(false); setRows(''); }}
              className="px-4 py-2.5 text-sm font-medium text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
            >
              Cancelar
            </button>
            <span className="text-[11px] text-slate-400 ml-auto">
              {rows.split('\n').filter(l => l.trim()).length} líneas
            </span>
          </div>
        </div>
      )}

      {/* Hoja de Google como origen permanente */}
      <div className="rounded-2xl border border-slate-200 dark:border-white/8 bg-white dark:bg-slate-card p-4">
        <div className="flex items-start gap-3">
          <span className={`material-symbols-outlined text-[20px] mt-0.5 shrink-0 ${hoja.url ? 'text-emerald-500' : 'text-slate-400'}`}>
            {hoja.url ? 'cloud_done' : 'cloud_off'}
          </span>

          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-900 dark:text-white flex items-center">
              Hoja de leads
              <Tooltip
                text="Se lee sola cada hora y solo entran los leads nuevos: los repetidos se descartan por teléfono, así que la IA puede seguir añadiendo filas sin que nadie marque nada."
                example="La hoja debe estar compartida como 'Cualquiera con el enlace'"
              />
            </p>

            {!editandoHoja && (
              <>
                {hoja.url ? (
                  <>
                    <a
                      href={hoja.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-emerald-600 dark:text-emerald-400 hover:underline break-all"
                    >
                      Abrir hoja conectada ↗
                    </a>
                    <p className="text-[11px] text-slate-400 mt-1">
                      {hoja.ultima
                        ? `Última lectura: ${new Date(hoja.ultima.fecha).toLocaleString('es-DO', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })} · ${hoja.ultima.leidas} filas · ${hoja.ultima.importados} nuevos`
                        : 'Aún no se ha leído — se leerá sola dentro de una hora, o pulsa Leer ahora.'}
                    </p>
                  </>
                ) : (
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 leading-relaxed">
                    Conecta una hoja de Google y los leads entrarán solos cada hora, sin importar nada a mano.
                  </p>
                )}
              </>
            )}

            {editandoHoja && (
              <div className="mt-2 space-y-2">
                <input
                  value={hojaInput}
                  onChange={e => setHojaInput(e.target.value)}
                  placeholder="https://docs.google.com/spreadsheets/d/..."
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 dark:bg-slate-900 dark:text-white text-xs focus:outline-none focus:ring-2 focus:ring-emerald-400"
                />
                <p className="text-[11px] text-slate-400">
                  En la hoja: Compartir → Acceso general → <strong>Cualquiera con el enlace</strong> (Lector).
                  Se comprueba al guardar. Déjalo vacío para desconectarla.
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={guardarHoja}
                    disabled={busy === 'hoja'}
                    className="px-3 py-1.5 rounded-xl text-xs font-semibold bg-emerald-500 hover:bg-emerald-600 text-white disabled:opacity-50"
                  >
                    {busy === 'hoja' ? 'Comprobando...' : 'Guardar'}
                  </button>
                  <button
                    onClick={() => setEditandoHoja(false)}
                    className="px-3 py-1.5 text-xs font-medium text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            )}
          </div>

          {!editandoHoja && (
            <div className="flex items-center gap-2 shrink-0">
              {hoja.url && (
                <button
                  onClick={sincronizarHoja}
                  disabled={busy === 'sync'}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold bg-emerald-500 hover:bg-emerald-600 text-white disabled:opacity-50"
                >
                  <span className={`material-symbols-outlined text-[15px] ${busy === 'sync' ? 'animate-spin' : ''}`}>
                    {busy === 'sync' ? 'progress_activity' : 'sync'}
                  </span>
                  {busy === 'sync' ? 'Leyendo...' : 'Leer ahora'}
                </button>
              )}
              <button
                onClick={() => { setHojaInput(hoja.url || ''); setEditandoHoja(true); }}
                className="px-3 py-1.5 rounded-xl text-xs font-semibold border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
              >
                {hoja.url ? 'Cambiar' : 'Conectar'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Detalle de lo descartado en la última importación */}
      {detalle && (
        <div className="rounded-2xl border border-amber-200 dark:border-amber-900/40 bg-amber-50 dark:bg-amber-950/20 px-4 py-3 space-y-2">
          <div className="flex items-start justify-between gap-2">
            <p className="text-sm font-semibold text-amber-900 dark:text-amber-300">
              Filas que no se importaron
            </p>
            <button
              onClick={() => setDetalle(null)}
              className="text-amber-700/70 dark:text-amber-400/70 hover:text-amber-900 dark:hover:text-amber-200"
              title="Cerrar"
            >
              <span className="material-symbols-outlined text-[18px]">close</span>
            </button>
          </div>

          {detalle.totalDup > 0 && (
            <div>
              <p className="text-xs font-semibold text-amber-800 dark:text-amber-400">
                {detalle.totalDup} repetidos — ya estaban en la lista, no se les escribirá dos veces
              </p>
              <ul className="mt-1 text-[11px] font-mono text-amber-700/80 dark:text-amber-400/80 space-y-0.5">
                {detalle.duplicados.map((d, i) => <li key={i}>· {d}</li>)}
                {detalle.totalDup > detalle.duplicados.length && (
                  <li className="italic">· y {detalle.totalDup - detalle.duplicados.length} más</li>
                )}
              </ul>
            </div>
          )}

          {detalle.totalInv > 0 && (
            <div>
              <p className="text-xs font-semibold text-amber-800 dark:text-amber-400">
                {detalle.totalInv} descartados — el teléfono no es válido
              </p>
              <ul className="mt-1 text-[11px] font-mono text-amber-700/80 dark:text-amber-400/80 space-y-0.5">
                {detalle.invalidos.map((d, i) => <li key={i}>· {d}</li>)}
                {detalle.totalInv > detalle.invalidos.length && (
                  <li className="italic">· y {detalle.totalInv - detalle.invalidos.length} más</li>
                )}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* ─── Listado de Conversaciones y Contactos de Prospección ─── */}
      {total > 0 && (
        <div className="bg-white dark:bg-slate-card rounded-2xl border border-slate-200 dark:border-white/8 overflow-hidden shadow-sm">
          <div className="p-4 border-b border-slate-100 dark:border-slate-800 space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div>
                <h2 className="text-base font-bold text-slate-900 dark:text-white flex items-center gap-2">
                  <span className="material-symbols-outlined text-emerald-500">forum</span>
                  Conversaciones de Prospección
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                    {leads.length}
                  </span>
                </h2>
                <p className="text-xs text-slate-400 mt-0.5">
                  Respuestas, resúmenes ejecutivos y datos extraídos por Diana en tiempo real
                </p>
              </div>

              {/* Buscador */}
              <div className="relative min-w-[200px] sm:max-w-xs w-full">
                <span className="material-symbols-outlined absolute left-3 top-2.5 text-slate-400 text-[18px]">search</span>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Buscar clínica, teléfono o resumen..."
                  className="w-full pl-9 pr-3 py-1.5 rounded-xl text-xs bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            </div>

            {/* Filtro por País */}
            <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-none border-b border-slate-100 dark:border-slate-800 pb-2">
              <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mr-1 shrink-0 flex items-center gap-1">
                <span className="material-symbols-outlined text-[14px]">public</span> País:
              </span>
              {[
                { id: 'all', label: 'Todos', flag: '🌐', count: leads.length },
                ...(countryCounts.PA > 0 ? [{ id: 'PA', label: 'Panamá', flag: '🇵🇦', count: countryCounts.PA }] : []),
                ...(countryCounts.DO > 0 ? [{ id: 'DO', label: 'Rep. Dominicana', flag: '🇩🇴', count: countryCounts.DO }] : []),
                ...(countryCounts.EC > 0 ? [{ id: 'EC', label: 'Ecuador', flag: '🇪🇨', count: countryCounts.EC }] : []),
                ...(countryCounts.CR > 0 ? [{ id: 'CR', label: 'Costa Rica', flag: '🇨🇷', count: countryCounts.CR }] : []),
                ...(countryCounts.MX > 0 ? [{ id: 'MX', label: 'México', flag: '🇲🇽', count: countryCounts.MX }] : []),
                ...(countryCounts.CO > 0 ? [{ id: 'CO', label: 'Colombia', flag: '🇨🇴', count: countryCounts.CO }] : []),
              ].map(countryTab => (
                <button
                  key={countryTab.id}
                  onClick={() => setCountryFilter(countryTab.id)}
                  className={`flex-none flex items-center gap-1.5 px-3 py-1 rounded-xl text-xs font-semibold transition-all border ${
                    countryFilter === countryTab.id
                      ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                      : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
                  }`}
                >
                  <span>{countryTab.flag}</span>
                  <span>{countryTab.label}</span>
                  <span className="opacity-75 text-[10px]">({countryTab.count})</span>
                </button>
              ))}
            </div>

            {/* Pestañas de Filtro por Estado */}
            <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-none">
              {[
                { id: 'all', label: 'Todos los Estados', count: stats.sent + stats.pending + stats.failed, icon: 'list' },
                { id: 'replied', label: '🔥 Respondieron', count: stats.replied, icon: 'auto_awesome' },
                { id: 'sent', label: '⏳ Enviados', count: Math.max(0, stats.sent - stats.replied), icon: 'schedule' },
                { id: 'pending', label: '📋 Pendientes', count: stats.pending, icon: 'queue' },
                { id: 'failed', label: '❌ Descartados', count: stats.failed, icon: 'block' },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setLeadFilter(tab.id as any)}
                  className={`flex-none flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-semibold transition-all ${
                    leadFilter === tab.id
                      ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 shadow-sm'
                      : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
                  }`}
                >
                  <span className="material-symbols-outlined text-[14px]">{tab.icon}</span>
                  {tab.label}
                  <span className="ml-1 opacity-70 text-[10px]">({tab.count})</span>
                </button>
              ))}
            </div>
          </div>

          {/* Contenido de la lista */}
          {leadsLoading ? (
            <div className="flex items-center justify-center py-16 text-slate-400">
              <span className="material-symbols-outlined animate-spin text-[28px] mr-2">progress_activity</span>
              <span className="text-sm">Cargando conversaciones...</span>
            </div>
          ) : leads.filter(l => {
              if (countryFilter !== 'all') {
                const cInfo = getCountryInfo(l);
                if (cInfo.code !== countryFilter) return false;
              }
              if (!searchQuery.trim()) return true;
              const q = searchQuery.toLowerCase();
              const cInfo = getCountryInfo(l);
              return (
                l.clinicName?.toLowerCase().includes(q) ||
                l.phone.includes(q) ||
                l.clinicCity?.toLowerCase().includes(q) ||
                l.clinicCountry?.toLowerCase().includes(q) ||
                cInfo.name.toLowerCase().includes(q) ||
                l.leadSummary?.toLowerCase().includes(q) ||
                l.followupNotes?.toLowerCase().includes(q) ||
                l.email?.toLowerCase().includes(q)
              );
            }).length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center text-slate-400">
              <span className="material-symbols-outlined text-[36px] mb-2 text-slate-300 dark:text-slate-600">speaker_notes_off</span>
              <p className="text-sm font-medium">No se encontraron prospectos en este filtro</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100 dark:divide-slate-800">
              {leads
                .filter(l => {
                  if (countryFilter !== 'all') {
                    const cInfo = getCountryInfo(l);
                    if (cInfo.code !== countryFilter) return false;
                  }
                  if (!searchQuery.trim()) return true;
                  const q = searchQuery.toLowerCase();
                  const cInfo = getCountryInfo(l);
                  return (
                    l.clinicName?.toLowerCase().includes(q) ||
                    l.phone.includes(q) ||
                    l.clinicCity?.toLowerCase().includes(q) ||
                    l.clinicCountry?.toLowerCase().includes(q) ||
                    cInfo.name.toLowerCase().includes(q) ||
                    l.leadSummary?.toLowerCase().includes(q) ||
                    l.followupNotes?.toLowerCase().includes(q) ||
                    l.email?.toLowerCase().includes(q)
                  );
                })
                .map(lead => {
                  const countryInfo = getCountryInfo(lead);
                  const cfg = stateConfig[lead.state] || { label: lead.state, color: 'text-slate-600', bg: 'bg-slate-100', icon: 'person' };
                  const isReplied = lead.outreachReplied;
                  const isCalificado = isReplied && (lead.email || lead.leadSummary?.toLowerCase().includes('calificado'));
                  const isDerivacion = isReplied && (lead.followupNotes?.toLowerCase().includes('referido') || lead.leadSummary?.toLowerCase().includes('derivaci'));
                  const isAutoReply = lead.leadSummary?.toLowerCase().includes('auto-respuesta') || lead.leadSummary?.toLowerCase().includes('autorespondedor');
                  const isEmailSent = /propuesta\s+(?:enviada|por\s+correo|remitida)|correo\s+enviado|email\s+enviado|✉️\s*propuesta/i.test(`${lead.followupNotes || ''} ${lead.leadSummary || ''}`);

                  return (
                    <div
                      key={lead.id}
                      className="p-4 hover:bg-slate-50/80 dark:hover:bg-slate-800/30 transition-colors space-y-2.5"
                    >
                      {/* Fila Principal */}
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                        <div className="flex items-start gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                            isCalificado
                              ? 'bg-amber-100 dark:bg-amber-950 text-amber-600 dark:text-amber-400'
                              : isReplied
                              ? 'bg-emerald-100 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400'
                              : 'bg-slate-100 dark:bg-slate-800 text-slate-500'
                          }`}>
                            <span className="material-symbols-outlined text-[20px]">
                              {isCalificado ? 'local_fire_department' : isReplied ? 'forum' : 'domain'}
                            </span>
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="text-sm font-bold text-slate-900 dark:text-white truncate">
                                {lead.clinicName || lead.name || lead.phone}
                              </h3>

                              {/* Badge de País */}
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border flex items-center gap-1 shrink-0 ${countryInfo.badgeClass}`}>
                                <span className="text-[12px]">{countryInfo.flag}</span>
                                {countryInfo.name}
                              </span>

                              {/* Badge de Hito */}
                              {isCalificado && (
                                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800 dark:bg-amber-950/80 dark:text-amber-300 border border-amber-300 dark:border-amber-800 flex items-center gap-1">
                                  <span className="material-symbols-outlined text-[12px]">local_fire_department</span>
                                  LEAD CALIFICADO
                                </span>
                              )}
                              {isEmailSent && (
                                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800 dark:bg-emerald-950/80 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-800 flex items-center gap-1 shadow-sm">
                                  <span className="material-symbols-outlined text-[12px] text-emerald-600 dark:text-emerald-400">mark_email_read</span>
                                  PROPUESTA ENVIADA
                                </span>
                              )}
                              {isDerivacion && !isCalificado && (
                                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 text-blue-800 dark:bg-blue-950/80 dark:text-blue-300 border border-blue-300 dark:border-blue-800 flex items-center gap-1">
                                  <span className="material-symbols-outlined text-[12px]">call_split</span>
                                  DERIVACIÓN A ADMINISTRACIÓN
                                </span>
                              )}
                              {isAutoReply && (
                                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-purple-100 text-purple-800 dark:bg-purple-950/80 dark:text-purple-300 border border-purple-300 dark:border-purple-800 flex items-center gap-1">
                                  <span className="material-symbols-outlined text-[12px]">smart_toy</span>
                                  AUTO-RESPUESTA
                                </span>
                              )}
                              {isReplied && !isCalificado && !isDerivacion && !isAutoReply && (
                                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800 dark:bg-emerald-950/80 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-800 flex items-center gap-1">
                                  <span className="material-symbols-outlined text-[12px]">chat</span>
                                  EN CONVERSACIÓN
                                </span>
                              )}

                              {/* Estado Embudo */}
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${cfg.bg} ${cfg.color}`}>
                                {cfg.label}
                              </span>
                            </div>

                            <div className="flex items-center gap-2 mt-0.5 text-xs text-slate-500 dark:text-slate-400 flex-wrap">
                              <span className="font-mono text-slate-700 dark:text-slate-300">{lead.phone}</span>
                              {lead.clinicCity && <span>· {lead.clinicCity}</span>}
                              {lead.updatedAt && (
                                <span className="text-[11px] text-slate-400">· {timeAgo(lead.updatedAt)}</span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Acciones Rápidas */}
                        <div className="flex items-center gap-1.5 shrink-0 self-end sm:self-center">
                          {(isCalificado || lead.email) && (
                            <button
                              onClick={() => setEmailProposalLead(lead)}
                              className={`flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-bold transition-all shadow-sm ${
                                isEmailSent
                                  ? 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-emerald-600/20'
                                  : 'bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:opacity-95 shadow-amber-500/20'
                              }`}
                              title={isEmailSent ? 'Propuesta ya enviada — Clic para ver o reenviar' : 'Plantilla de correo y propuesta personalizada'}
                            >
                              <span className="material-symbols-outlined text-[15px]">
                                {isEmailSent ? 'task_alt' : 'mark_email_read'}
                              </span>
                              {isEmailSent ? 'Propuesta Enviada' : 'Plantilla Correo'}
                            </button>
                          )}
                          <button
                            onClick={() => openLead(lead)}
                            className="flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-semibold bg-indigo-50 dark:bg-indigo-950/50 text-indigo-600 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/60 transition-colors"
                          >
                            <span className="material-symbols-outlined text-[15px]">chat_bubble</span>
                            Ver Conversación
                          </button>
                          <a
                            href={`https://wa.me/${lead.phone.replace(/\D/g, '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-8 h-8 rounded-xl flex items-center justify-center bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900/60 transition-colors"
                            title="Abrir en WhatsApp"
                          >
                            <span className="material-symbols-outlined text-[16px]">chat</span>
                          </a>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(lead.phone);
                              showToast('Teléfono copiado');
                            }}
                            className="w-8 h-8 rounded-xl flex items-center justify-center bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 transition-colors"
                            title="Copiar teléfono"
                          >
                            <span className="material-symbols-outlined text-[16px]">content_copy</span>
                          </button>
                        </div>
                      </div>

                      {/* Resumen Ejecutivo de Diana */}
                      {(lead.leadSummary || lead.followupNotes || lead.email) && (
                        <div className="rounded-xl border border-indigo-100 dark:border-indigo-900/50 bg-gradient-to-br from-indigo-50/70 via-white to-purple-50/30 dark:from-indigo-950/30 dark:via-slate-900/40 dark:to-purple-950/20 p-3 text-xs space-y-1.5">
                          {lead.leadSummary && (
                            <div className="flex items-start gap-1.5 text-slate-800 dark:text-slate-200 leading-relaxed">
                              <span className="material-symbols-outlined text-[15px] text-indigo-500 shrink-0 mt-0.5">auto_awesome</span>
                              <span className="font-medium whitespace-pre-wrap">{lead.leadSummary}</span>
                            </div>
                          )}

                          <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-indigo-100/60 dark:border-indigo-900/30 text-[11px]">
                            {lead.email && (
                              <button
                                onClick={() => setEmailProposalLead(lead)}
                                className={`px-2 py-0.5 rounded-md font-semibold flex items-center gap-1 transition-colors ${
                                  isEmailSent
                                    ? 'bg-emerald-100 hover:bg-emerald-200 dark:bg-emerald-950/90 text-emerald-800 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-800'
                                    : 'bg-emerald-100 hover:bg-emerald-200 dark:bg-emerald-950/80 dark:hover:bg-emerald-900/80 text-emerald-800 dark:text-emerald-300'
                                }`}
                                title={isEmailSent ? 'Propuesta ya enviada — Clic para abrir o reenviar' : 'Click para abrir plantilla de propuesta por correo'}
                              >
                                <span className="material-symbols-outlined text-[12px]">
                                  {isEmailSent ? 'mark_email_read' : 'mail'}
                                </span>
                                <span>{isEmailSent ? '✓ Propuesta enviada a:' : 'Email:'} {lead.email}</span>
                                <span className="material-symbols-outlined text-[12px] ml-0.5 text-emerald-600">
                                  {isEmailSent ? 'check' : 'send'}
                                </span>
                              </button>
                            )}
                            {lead.followupNotes && (
                              <span className="px-2 py-0.5 rounded-md bg-indigo-100/70 dark:bg-indigo-950/60 text-indigo-800 dark:text-indigo-300 font-medium flex items-center gap-1">
                                <span className="material-symbols-outlined text-[12px]">contact_phone</span>
                                {lead.followupNotes}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      )}

      {/* Modal de Detalle de Lead y Chat */}
      {selectedLead && (
        <LeadDetailModal
          lead={selectedLead}
          messages={messages}
          loadingMessages={loadingMessages}
          updatingState={updatingState}
          convertingPlan={convertingPlan}
          onClose={() => setSelectedLead(null)}
          onStateChange={handleStateChange}
          onConvert={handleConvert}
        />
      )}

      {/* Modal de Plantilla de Correo Personalizado (Resend) */}
      {emailProposalLead && (
        <LeadEmailProposalModal
          lead={emailProposalLead}
          onClose={() => setEmailProposalLead(null)}
          onSent={(to) => {
            showToast(`Propuesta enviada exitosamente a ${to}`, 'success');
            loadLeads(leadFilter);
          }}
        />
      )}

      {/* Estado vacío / explicación */}
      {total === 0 && !showImport && (
        <div className="flex flex-col items-center justify-center py-12 text-center gap-2">
          <span className="material-symbols-outlined text-[48px] text-slate-300 dark:text-slate-600">contacts</span>
          <p className="text-sm text-slate-500 dark:text-slate-400">Aún no has importado ninguna lista</p>
          <p className="text-xs text-slate-400 max-w-sm">
            Pega los teléfonos y nombres de clínicas desde tu Excel. Diana enviará el primer mensaje
            de forma escalonada y seguirá la conversación con quienes respondan.
          </p>
        </div>
      )}

      {stats.pending > 0 && !stats.enabled && (
        <p className="text-[11px] text-slate-400 leading-relaxed text-center">
          Al activar, Diana escribe a {stats.pending} {stats.pending === 1 ? 'clínica' : 'clínicas'} de forma
          escalonada. Cuando alguna responde, continúa la conversación sola y la califica.
        </p>
      )}
    </div>
  );
};

export default Outreach;

